import { useMemo } from "react";
import { defineMessages } from "react-intl";

import Button from "../Buttons/Button";
import { useFormatMessage } from "../Intl/Context";
import TreeSelectInput from "./TreeSelectInput";
import inputConnect from "./inputConnect";
import { useInputAriaDescribedBy } from "../Form/useInputAriaDescribedBy";

const TreeMultiSelectInput = props => {
  const {
    formKey,
    fieldName,
    options,
    updateValue,
    maxSelectedOptions,
    required,
    values,
    selectOptions = {}
  } = props;

  // Depending on the provided input values, and the required state of the field, we
  // prepare the right type of selectedValues to use in our field.
  //
  // Required fields should always have at least one pre-added row, even if the provided
  // input values is an empty array.
  //
  // Optional fields with no input values don't need to display a default field.
  const selectedValues = useMemo(() => {
    if (values?.length) {
      return values;
    } else if (!values && !required) {
      return values;
    } else {
      return [null];
    }
  }, [required, values]);

  const t = useFormatMessage();
  const addButtonLabel = props.addButtonLabel || t(intlMessages.addButtonLabel);

  const ariaDescribedBy = useInputAriaDescribedBy();

  const handleAddButtonClicked = () => {
    const inputValues = [...selectedValues, null];
    updateValue({ values: inputValues });
  };

  const handleRemoveButtonClicked = index => {
    const inputValues = [...selectedValues];
    inputValues.splice(index, 1);

    updateValue({ values: inputValues });

    if (props.onChange) {
      props.onChange(inputValues);
    }
  };

  const handleOptionChanged = (option, index) => {
    const inputValues = [...selectedValues];
    inputValues[index] = option?.value;
    updateValue({ values: inputValues });

    if (props.onChange) {
      props.onChange(inputValues);
    }
  };

  // Hide the Add button if we reached the maximum selectable options count
  const displayAddButton = selectedValues.length < maxSelectedOptions;
  // If the field is required and there's only one displayed option, hide the Remove
  // button so the user cannot get an empty required field
  const displayRemoveButton = !required || selectedValues.length > 1;

  return (
    <div className="tree-multi-select-input">
      <div className="tree-multi-select-fields">
        {selectedValues.map((value, index) => (
          <div key={`${fieldName}-${index}`} className="tree-multi-select-fields d-flex">
            <TreeSelectInput
              {...{ formKey, options }}
              fieldName={`${fieldName}-${index}`}
              onChange={option => handleOptionChanged(option, index)}
              value={{ value: value }}
              disableValueUpdate={true}
              className="flex-1"
              childSelectsClassName="flex-1"
              ariaDescribedBy={index === 0 ? ariaDescribedBy : null}
              {...selectOptions}
            />

            {displayRemoveButton && (
              <Button
                type="button"
                className="btn btn-danger-inverse btn-sm"
                onClick={() => handleRemoveButtonClicked(index)}
                iconClassName="icon-suppr"
              />
            )}
          </div>
        ))}
      </div>

      {displayAddButton && (
        <Button
          type="button"
          className="btn btn-primary-inverse btn-sm margin-top-sm"
          onClick={handleAddButtonClicked}
          iconClassName="icon-plus"
          label={addButtonLabel}
        />
      )}
    </div>
  );
};

export default inputConnect(TreeMultiSelectInput, {
  fields: { values: [] }
});

const intlMessages = defineMessages({
  addButtonLabel: {
    id: "input.treeMultiSelect.addButtonLabel",
    defaultMessage: "Ajouter un champ"
  }
});
